'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Track } from 'livekit-client';
import { AnimatePresence, motion } from 'motion/react';
import { VideoTrack, useLocalParticipant, useRoomContext, useVoiceAssistant, } from '@livekit/components-react';
import { ActionBar } from '@/components/embed-popup/action-bar';
import { AudioVisualizer } from '@/components/embed-popup/audio-visualizer';
import { ConsentOverlay } from '@/components/embed-popup/consent-overlay';
import { ShareBanner } from '@/components/embed-popup/share-banner';
import { Transcript } from '@/components/embed-popup/transcript';
import useChatAndTranscription from '@/hooks/use-chat-and-transcription';
import { useScreenshareCallerConsentNotifier, useScreenshareStopNotifier, } from '@/hooks/use-screenshare-peer';
import { useScreenshareSession } from '@/hooks/use-screenshare-session';
import { useDebugMode } from '@/hooks/useDebug';
import { cn } from '@/lib/utils';
const TILE_TRANSITION = {
    type: 'spring',
    stiffness: 675,
    damping: 75,
    mass: 1,
};
const TranscriptMotion = motion.create(Transcript);
export function useLocalTrackRef(source) {
    const { localParticipant } = useLocalParticipant();
    const publication = localParticipant.getTrackPublication(source);
    const trackRef = useMemo(() => (publication ? { source, participant: localParticipant, publication } : undefined), [source, publication, localParticipant]);
    return trackRef;
}
function isAgentAvailable(agentState) {
    return agentState == 'listening' || agentState == 'thinking' || agentState == 'speaking';
}
export const PopupView = ({ appConfig, disabled, sessionStarted, onEmbedError, ref, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    useDebugMode();
    const room = useRoomContext();
    const { state: agentState, audioTrack: agentAudioTrack, videoTrack: agentVideoTrack, } = useVoiceAssistant();
    const { isCameraEnabled, isScreenShareEnabled } = useLocalParticipant();
    const cameraTrack = useLocalTrackRef(Track.Source.Camera);
    // The caller's OWN screen track. `useTracks` would also return a remote one.
    const localScreenShareTrack = useLocalTrackRef(Track.Source.ScreenShare);
    const [chatOpen, setChatOpen] = useState(false);
    const [sharePending, setSharePending] = useState(false);
    const { messages, send } = useChatAndTranscription();
    // TLZ-561. One ended share, one `screenshare.notify`: the session hook reports a stop
    // exactly once, from its single unpublish listener, and this turns that report into the
    // notification. Sending it from anywhere else would race that listener.
    const notifyStopped = useScreenshareStopNotifier();
    // TLZ-561. A share the caller started from the widget's own button is announced to the
    // agent with the picker's outcome; without it the agent records it as pre-existing.
    const notifyCallerConsent = useScreenshareCallerConsentNotifier();
    // TLZ-561. Owns the consent prompt and the screen track. Whether a share may be offered
    // is derived inside the hook from the live publish permission (widened by the worker at
    // runtime) and the agent's attributes, so nothing is threaded in from the token route.
    const { canShare, consentRequest, acceptConsent, declineConsent, startShare, stopShare } = useScreenshareSession({ onStopped: notifyStopped, onCallerConsent: notifyCallerConsent });
    const { supportsChatInput, supportsVideoInput } = appConfig;
    const capabilities = {
        supportsChatInput,
        supportsVideoInput,
        // The worker widened the permission, this browser can capture a display, and an agent
        // that can receive the share is in the room.
        supportsScreenShare: canShare,
    };
    /** The one Stop path the widget owns, shared by the banner and the control bar. */
    const handleStopShare = useCallback(async () => {
        setSharePending(true);
        try {
            await stopShare('caller_stop');
        }
        finally {
            setSharePending(false);
        }
    }, [stopShare]);
    const handleShareToggle = useCallback((pressed) => {
        void (async () => {
            if (!pressed) {
                await handleStopShare();
                return;
            }
            setSharePending(true);
            try {
                await startShare();
            }
            finally {
                setSharePending(false);
            }
        })();
    }, [handleStopShare, startShare]);
    async function onSendMessage(message) {
        await send(message);
        return;
    }
    // If the agent hasn't connected after an interval,
    // then show an error - something must not be working
    useEffect(() => {
        if (!sessionStarted) {
            return;
        }
        const timeout = setTimeout(() => {
            if (!isAgentAvailable(agentState)) {
                const reason = agentState === 'connecting'
                    ? 'Agent did not join the room. '
                    : 'Agent connected but did not complete initializing. ';
                onEmbedError({
                    title: 'Session ended',
                    description: _jsx("p", { className: "w-full", children: reason }),
                });
            }
        }, 10000);
        return () => clearTimeout(timeout);
    }, [agentState, sessionStarted, room, onEmbedError]);
    return (_jsx("div", { ref: ref, inert: disabled, className: "flex h-full w-full flex-col overflow-hidden", children: _jsxs("div", { className: "relative flex h-full shrink-1 grow-1 flex-col", children: [_jsx(TranscriptMotion, { initial: {
                        y: 10,
                        opacity: 0,
                    }, animate: {
                        y: chatOpen ? 0 : 10,
                        opacity: chatOpen ? 1 : 0,
                    }, transition: {
                        type: 'spring',
                        duration: 0.5,
                        bounce: 0,
                    }, messages: messages }), _jsx(AnimatePresence, { children: !agentVideoTrack && (_jsx(motion.div, { initial: {
                            scale: 1,
                            left: '50%',
                            top: '50%',
                            translateX: '-50%',
                            translateY: '-50%',
                            transformOrigin: 'center top',
                        }, animate: {
                            left: chatOpen && (isCameraEnabled || isScreenShareEnabled) ? '39%' : '50%',
                            scale: chatOpen ? 0.275 : 1,
                            top: chatOpen ? '12px' : '50%',
                            translateY: chatOpen ? '0' : '-50%',
                            transformOrigin: chatOpen ? 'center top' : 'center center',
                        }, transition: TILE_TRANSITION, className: cn('bg-bg1 dark:bg-bg2 pointer-events-none absolute flex aspect-square w-64 items-center justify-center rounded-2xl border border-transparent transition-colors', chatOpen && 'border-separator1 dark:border-separator2 drop-shadow-2xl'), children: _jsx(AudioVisualizer, { agentState: agentState, audioTrack: agentAudioTrack }) }, "audio-visualizer")) }), _jsx(AnimatePresence, { children: agentVideoTrack && (_jsx(motion.div, { initial: {
                            maskImage: 'radial-gradient(circle, rgba(0, 0, 0, 1) 0, rgba(0, 0, 0, 1) 40px, transparent 40px)',
                            filter: 'blur(20px)',
                        }, animate: {
                            opacity: chatOpen ? 0 : 1,
                            maskImage: 'radial-gradient(circle, rgba(0, 0, 0, 1) 0, rgba(0, 0, 0, 1) 500px, transparent 500px)',
                            filter: 'blur(0px)',
                        }, transition: {
                            opacity: {
                                ease: 'linear',
                                duration: 0.2,
                            },
                            maskImage: {
                                ease: 'linear',
                                duration: 1,
                            },
                            filter: {
                                ease: 'linear',
                                duration: 1,
                            },
                        }, className: "border-separator1 dark:border-separator2 pointer-events-none absolute inset-1 drop-shadow-lg/20", children: _jsx(VideoTrack, { trackRef: agentVideoTrack, width: (_b = (_a = agentVideoTrack === null || agentVideoTrack === void 0 ? void 0 : agentVideoTrack.publication.dimensions) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 0, height: (_d = (_c = agentVideoTrack === null || agentVideoTrack === void 0 ? void 0 : agentVideoTrack.publication.dimensions) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : 0, className: "h-full rounded-[24px] bg-black object-cover" }) }, "avatar")) }), _jsx(AnimatePresence, { children: agentVideoTrack && chatOpen && (_jsx(motion.div, { initial: {
                            opacity: 0,
                            scale: 0.5,
                            left: isCameraEnabled || isScreenShareEnabled ? '39%' : '50%',
                            top: '12px',
                            translateX: '-50%',
                            transformOrigin: 'center top',
                        }, animate: {
                            opacity: 1,
                            scale: 1,
                            left: isCameraEnabled || isScreenShareEnabled ? '37.5%' : '50%',
                        }, transition: TILE_TRANSITION, className: "border-separator1 dark:border-separator2 pointer-events-none absolute drop-shadow-lg/20", children: _jsx(VideoTrack, { trackRef: agentVideoTrack, width: (_f = (_e = agentVideoTrack === null || agentVideoTrack === void 0 ? void 0 : agentVideoTrack.publication.dimensions) === null || _e === void 0 ? void 0 : _e.width) !== null && _f !== void 0 ? _f : 0, height: (_h = (_g = agentVideoTrack === null || agentVideoTrack === void 0 ? void 0 : agentVideoTrack.publication.dimensions) === null || _g === void 0 ? void 0 : _g.height) !== null && _h !== void 0 ? _h : 0, className: "aspect-square w-[70px] rounded-md bg-black object-cover" }) }, "audio-visualizer")) }), _jsx(AnimatePresence, { children: cameraTrack && isCameraEnabled && (_jsx(motion.div, { initial: {
                            scale: 0.5,
                            opacity: 0,
                            right: '12px',
                            top: '346px',
                            transformOrigin: 'center bottom',
                        }, animate: {
                            scale: 1,
                            opacity: 1,
                            top: chatOpen ? '12px' : '346px',
                            right: chatOpen ? '106px' : '12px',
                            transformOrigin: chatOpen ? 'center top' : 'center bottom',
                        }, exit: {
                            scale: 0.5,
                            opacity: 0,
                        }, transition: TILE_TRANSITION, className: "border-separator1 dark:border-separator2 pointer-events-none absolute drop-shadow-lg/20", children: _jsx(VideoTrack, { trackRef: cameraTrack, width: (_k = (_j = cameraTrack === null || cameraTrack === void 0 ? void 0 : cameraTrack.publication.dimensions) === null || _j === void 0 ? void 0 : _j.width) !== null && _k !== void 0 ? _k : 0, height: (_m = (_l = cameraTrack === null || cameraTrack === void 0 ? void 0 : cameraTrack.publication.dimensions) === null || _l === void 0 ? void 0 : _l.height) !== null && _m !== void 0 ? _m : 0, className: "aspect-square w-[70px] rounded-md bg-black object-cover" }) }, "camera")) }), _jsx(motion.div, { initial: {
                        opacity: 0,
                        translateY: 8,
                    }, animate: {
                        opacity: sessionStarted ? 1 : 0,
                        translateY: sessionStarted ? 0 : 8,
                    }, transition: {
                        delay: 0.5,
                    }, children: _jsx(ActionBar, { capabilities: capabilities, 
                        // Driven by the screenshare session rather than LiveKit's own track toggle,
                        // so a start records consent and a stop is attributed as `caller_stop`.
                        screenShareControl: {
                            // Publication state, so the control always reflects what is actually being
                            // published and the caller's own Stop can never be out of reach.
                            pressed: isScreenShareEnabled,
                            pending: sharePending,
                            onPressedChange: handleShareToggle,
                        }, onSendMessage: onSendMessage, onChatOpenChange: setChatOpen }) }), isScreenShareEnabled && (_jsx(ShareBanner, { trackRef: localScreenShareTrack, stopping: sharePending, onStop: () => {
                        void handleStopShare();
                    } })), consentRequest && (_jsx(ConsentOverlay, { agentName: appConfig.agentName, surfaces: consentRequest.surfaces, timeoutSeconds: consentRequest.timeoutSeconds, expiresAt: consentRequest.expiresAt, capturing: consentRequest.capturing, onAccept: () => {
                        void acceptConsent();
                    }, onDecline: declineConsent }))] }) }));
};
